// Step 1 (UPLOAD). Visual values pulled from Figma "AI Import" file:
//   - Upload card:  node 7230:11058
//   - Drafts card:  node 7230:11074  ("Past imports waiting for review:")
//   - Delete modal: node 7230:11866  ("Delete … import?")
// Design tokens mapped from theme/* + typography/* in that file.
import React, {
  useCallback, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/core';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import HeaderText from '../../../shared/components/HeaderText/HeaderText';
import Text from '../../../shared/components/Text/Text';
import {
  setFile, discoverPrograms, loadImportForReview, abandonImport, fetchAiProviderHealth,
} from '../ducks';

// Short, friendly relative-time label for draft rows. Matches the Figma copy
// ("Yesterday", "4 days ago", "6 days ago") — full words, day-level
// granularity. No new dependency; a dozen lines of date math beats pulling in
// date-fns / dayjs.
const formatRelativeTime = (input) => {
  if (!input) return '';
  const then = new Date(input);
  if (Number.isNaN(then.getTime())) return '';
  const now = new Date();
  // Calendar-day delta — robust against DST since we compare local dates.
  const midnightNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const midnightThen = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const days = Math.round((midnightNow - midnightThen) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return '1 week ago';
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 60) return '1 month ago';
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  if (days < 730) return '1 year ago';
  return `${Math.floor(days / 365)} years ago`;
};

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

// Step 1 stack — the upload Card with, when the coach has unfinished imports,
// a "Past imports waiting for review:" DraftsCard stacked 32px below it
// (Figma: upload card ends y=554, drafts card starts y=586).
const StepStack = styled('div')`
  width: 100%;
  max-width: 470px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
`;

// Card spec from Figma node 7230:11058: 470px wide, 16px radius, 1px #EEE
// border, Shadow/Theme/100 stack, 32px uniform padding, 16px gap between
// child blocks.
const Card = styled('div')`
  width: 100%;
  max-width: 470px;
  background: #FFFFFF;
  border: 1px solid #EEEEEE;
  border-radius: 16px;
  box-shadow:
    0 0 0.5px 0 rgba(48, 49, 51, 0.05),
    0 16px 12px 0 rgba(48, 49, 51, 0.10);
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;

  @media (max-width: 600px) {
    padding: 20px;
    border-radius: 12px;
  }
`;

// Drafts card — same surface spec as the upload Card (Figma node 7230:11074).
const DraftsCard = styled('div')`
  width: 100%;
  max-width: 470px;
  background: #FFFFFF;
  border: 1px solid #EEEEEE;
  border-radius: 16px;
  box-shadow:
    0 0 0.5px 0 rgba(48, 49, 51, 0.05),
    0 16px 12px 0 rgba(48, 49, 51, 0.10);
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;

  @media (max-width: 600px) {
    padding: 20px;
    border-radius: 12px;
  }
`;

// "Past imports waiting for review:" — Nunito Sans Bold (700) 19px #444
// (typography/Body/Bold) per Figma node 7230:11076.
const DraftsTitle = styled(HeaderText)`
  font-size: 19px;
  font-weight: 700;
  margin: 0;
  line-height: 1.5;
`;

const DraftsList = styled('ul')`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Each draft row: 1px #EEE bottom border + 16px bottom padding (Figma node
// 7230:11078). The last row drops the divider + padding.
const DraftRow = styled('li')`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #EEEEEE;

  &:last-of-type {
    padding-bottom: 0;
    border-bottom: none;
  }
`;

// Holds the name block + trash button. The 16px gap gives the trash button
// equal breathing room from the filename text — matching the 16px DraftRow
// gap to Resume, so the trash sits balanced between text and Resume (per PM
// feedback: equal space on both sides of the trash, not clustered to Resume).
const DraftLeft = styled('div')`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 16px;
`;

// flex: 1 so the text column owns the available width and the filename below
// can truncate with an ellipsis instead of pushing the row wider.
const DraftText = styled('div')`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

// Filename — Nunito Sans Bold (700) 14px #444 per Figma node 7242:3483
// (revised drafts card). Truncates with an ellipsis; the full name shows on
// hover via the react-tooltip wired up below.
const DraftName = styled(Text)`
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  line-height: 1.5;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Program/sheet name + relative time — Bold 12px #9E9E9E per Figma nodes
// 7230:11082 / 7230:11083 (the block inherits Nunito Sans Bold).
const DraftMeta = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  color: #9E9E9E;
  margin: 0;
  line-height: 1.5;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Trash button — 24px circle, bg #EEEEEE (theme/bg/subtle) with a #444444
// (theme/fg/default) glyph, per Figma node 7245:4444 in 7242:3889. Goes red
// on hover (node 7230:11565).
const DraftTrashButton = styled('button')`
  appearance: none;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: #EEEEEE;
  color: #444444;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    color: #FF0505;
    background: #FFD9D9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// "Resume" link button — Nunito Sans Bold (700) 13px #77A7FF with 0.39px
// tracking and a trailing chevron (Figma node 7230:11085). No background.
const DraftResumeButton = styled('button')`
  appearance: none;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  padding: 6px 16px 6px 0;
  color: #77A7FF;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.39px;
  line-height: 1.5;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover:not(:disabled) {
    color: #5B8BFF;
  }

  &:disabled {
    cursor: progress;
    opacity: 0.6;
  }
`;

// ---- Delete confirmation modal (Figma node 7230:11866) -------------------
// Backdrop + card sit at z-index 200 (same layer as the Navigator's confirm)
// so they land above the Step 1 surface but below any toasts.
const ConfirmOverlay = styled('div')`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
`;

// White card, 1px #EEE border, 8px radius, Shadow/Theme/100, 24px padding,
// 32px gap between the header block and the CTA stack.
const ConfirmCard = styled('div')`
  background: #FFFFFF;
  border: 1px solid #EEEEEE;
  border-radius: 8px;
  box-shadow:
    0 0 0.5px 0 rgba(48, 49, 51, 0.05),
    0 16px 12px 0 rgba(48, 49, 51, 0.10);
  padding: 24px;
  width: 600px;
  max-width: calc(100vw - 48px);
  display: flex;
  flex-direction: column;
  gap: 32px;
  box-sizing: border-box;
`;

const ConfirmHeader = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ConfirmTitleRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

// Title — Nunito Sans Bold (700) 24px #444, -0.24px tracking (node 7230:11870).
const ConfirmTitle = styled(HeaderText)`
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.24px;
  margin: 0;
  line-height: 1.2;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ConfirmClose = styled('button')`
  appearance: none;
  flex-shrink: 0;
  background: transparent;
  border: none;
  padding: 0;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #444444;
  cursor: pointer;

  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

// Body — Nunito Sans Regular (400) 16px #9E9E9E (node 7230:11872).
const ConfirmBody = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  color: #9E9E9E;
  margin: 0;
  line-height: 1.5;
`;

const ConfirmCTA = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

// Delete — bg theme/error/emphasis #FF0505, white Bold 16px / 0.48px tracking,
// 36px tall, full width (node 7230:11874).
const ConfirmDelete = styled('button')`
  width: 100%;
  height: 36px;
  background: #FF0505;
  border: none;
  border-radius: 4px;
  color: #FFFFFF;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.48px;
  line-height: 1.5;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) { background: #D70000; }
  &:disabled { cursor: not-allowed; opacity: 0.6; }
`;

// Cancel — ghost, #444 Bold 16px / 0.48px tracking, 36px tall (node 7230:11875).
const ConfirmCancel = styled('button')`
  width: 100%;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #444444;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.48px;
  line-height: 1.5;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) { background: #FAFAFB; }
  &:disabled { cursor: not-allowed; opacity: 0.6; }
`;

// Title block has no gap of its own — Figma relies on line-height: 1.5
// across the title + subtitle paragraphs to create the visual rhythm.
const TitleBlock = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.5;
  width: 100%;
`;

// Title — Nunito Sans Black (900), 24px, #444, line-height 1.5.
const Title = styled(HeaderText)`
  font-size: 24px;
  font-weight: 900;
  margin: 0;
  letter-spacing: 0;
  line-height: 1.5;
  width: 100%;

  @media (max-width: 600px) {
    font-size: 20px;
  }
`;

// Subtitle — Nunito Sans Regular (400), 16px, #9E9E9E, line-height 1.5.
const Subtitle = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  color: #9E9E9E;
  margin: 0;
  line-height: 1.5;
  width: 100%;
`;

// Plan-state notice. Figma node 7230:11069 renders this as inline text with a
// yellow (colors/yellow/200 #FFEAB5) highlight behind the count phrase only,
// followed by plain context. Both halves are Nunito Sans Bold 12px #444.
const NoticeInline = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  margin: 0;
  line-height: 1.5;
  width: 100%;
`;

const NoticeMark = styled('span')`
  background: #FFEAB5;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
`;

// Used-up notice box (Figma node 7230:10298): tinted yellow fill, 16px
// padding, no border, square corners (the design has no radius on this box).
// Headline + body stack with a full blank line between them — Figma separates
// the two sentences with an empty 18px line (one line-height), so the gap
// matches that rather than a tighter spacing. Only the "imports used up" state
// uses this box; states with imports left use the inline notice above.
const Notice = styled('div')`
  background: rgba(255, 203, 71, 0.2);
  padding: 16px;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const NoticeText = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  margin: 0;
  line-height: 1.5;
`;

// Footer line — Bold 12px #444 with #77A7FF underlined links. Active state
// (Figma node 7230:10510): "Upgrade for more imports. For questions, write
// support@teambuildr.com." — only "Upgrade" and the email link. The design
// keeps it on a single line (whitespace-nowrap); without this the email wraps
// to a second line, which the design does not do.
const FooterRow = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  margin: 0;
  line-height: 1.5;
  width: 100%;
  /* Single line to match the design (Figma 7230:10510). The card is a fixed
     470px on desktop, so the row fits; below the card's 600px breakpoint it
     would overflow, so we let it wrap there instead of forcing a scroll. */
  white-space: nowrap;

  @media (max-width: 600px) {
    white-space: normal;
  }

  a {
    display: inline;
    background: transparent;
    border: none;
    padding: 0;
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    color: #77A7FF;
    text-decoration: underline;
    text-underline-position: from-font;
    cursor: pointer;

    &:hover {
      color: #5B8BFF;
    }
  }
`;

// Provider-down warning. Distinct red-tinted box (vs the quota notice's
// yellow) so an outage reads as a blocker, not a nudge. Shown only when the
// healthcheck reports available === false.
const HealthWarning = styled('div')`
  background: rgba(255, 86, 86, 0.1);
  border: 1px solid rgba(255, 86, 86, 0.25);
  border-radius: 8px;
  padding: 12px 16px;
  width: 100%;
  box-sizing: border-box;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #C0392B;
  line-height: 1.5;
`;

// Dropzone — Figma node 7230:11062: 1px dashed #EEE on a #FCFCFC bg,
// 8px radius, 32px padding all sides, 8px gap between icon + texts.
const DropZoneWrapper = styled('div')`
  background: #FCFCFC;
  width: 100%;
`;

const DropZone = styled(({ isDragging, hasFile, ...rest }) => <div {...rest} />)`
  border: 1px dashed ${(props) => {
    if (props.isDragging) return '#0dcc8a';
    if (props.hasFile) return '#0dcc8a';
    return '#EEEEEE';
  }};
  border-radius: 8px;
  background: ${(props) => {
    if (props.isDragging) return 'rgba(13, 204, 138, 0.04)';
    if (props.hasFile) return 'rgba(13, 204, 138, 0.03)';
    return '#FCFCFC';
  }};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px;
  gap: 8px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    border-color: ${(props) => (props.hasFile ? '#0dcc8a' : '#DBDBDB')};
  }

  @media (max-width: 600px) {
    padding: 20px 16px;
  }
`;

// Icon circle — Figma node 7230:11064: bg #E0E0E0, fully rounded, 8px
// padding wrapping a 24px icon (40px outer diameter).
const IconWrap = styled(({ hasFile, ...rest }) => <div {...rest} />)`
  background: ${(props) => (props.hasFile ? 'rgba(13, 204, 138, 0.18)' : '#E0E0E0')};
  border-radius: 100px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;
  animation: ${(props) => (props.hasFile ? float : 'none')} 3s ease infinite;
`;

const DropZoneText = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  margin: 0;
  line-height: 1.5;
  text-align: center;
`;

const DropZoneSubText = styled(Text)`
  font-size: 12px;
  font-weight: 400;
  color: #9E9E9E;
  margin: 0;
  line-height: 1.5;
  text-align: center;
`;

const FileName = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  color: #0dcc8a;
  margin: 0;
  line-height: 1.5;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HiddenInput = styled('input')`
  display: none;
`;

// Import button — Figma node 7230:11068 (enabled): bg #444, white text,
// Nunito Sans Bold 13px with 0.39px tracking, 16px h-padding, 4px radius.
// Disabled state uses theme/accent/disabled (#E0E0E0) +
// theme/fg/on-disabled (#757575).
const ScanButton = styled(({ isLoading, ...rest }) => <button type='button' {...rest} />)`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => {
    if (props.disabled && !props.isLoading) return '#E0E0E0';
    return '#444444';
  }};
  color: ${(props) => {
    if (props.disabled && !props.isLoading) return '#757575';
    return '#FFFFFF';
  }};
  border: none;
  border-radius: 4px;
  padding: 9px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.39px;
  line-height: 1;
  text-transform: uppercase;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
  position: relative;
  overflow: hidden;

  ${(props) => props.isLoading && css`
    background: linear-gradient(90deg, #444444 0%, #5a5a5a 50%, #444444 100%);
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s ease infinite;
    color: #FFFFFF;
  `}

  &:hover:not(:disabled) {
    background: #2a2a2a;
    box-shadow: 0 4px 16px rgba(48, 49, 51, 0.25);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ErrorText = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  color: #C43D32;
  background: rgba(196, 61, 50, 0.08);
  border: 1px solid rgba(196, 61, 50, 0.2);
  border-radius: 8px;
  padding: 12px 16px;
  margin: 0;
  line-height: 1.5;
`;

const QuotaSkeleton = styled('div')`
  height: 56px;
  border-radius: 8px;
  background: #F4F4F4;
  opacity: 0.6;
`;

// MIME types are case-insensitive (RFC 2045) and OSes disagree on casing —
// macOS/Chrome report xlsm as '...macroenabled.12' while the official
// registration (and Windows) uses '...macroEnabled.12' — so keep this list
// lowercase and lowercase file.type before comparing.
const ACCEPTED_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroenabled.12', // xlsm — macros stripped server-side
  'text/csv', // csv
  'application/csv', // csv — some clients/browsers report this
];

// Extension is the reliable signal: CSV gets inconsistent MIME types across
// OSes (text/csv, application/vnd.ms-excel, text/plain, or empty), so we also
// accept on extension alone — mirroring the server's OR check.
const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.xlsm', '.csv'];

// Support inbox for the "Questions? … write support@teambuildr.com." footer.
const SUPPORT_EMAIL = 'support@teambuildr.com';

// Shared react-tooltip instance id for the draft filename hover tooltip. One
// <ReactTooltip> serves every row; each row supplies its own full filename via
// data-tooltip-content.
const DRAFT_NAME_TOOLTIP_ID = 'ai-import-draft-name';

// Fallback upgrade target when the backend's messaging omits upgradeUrl.
const UPGRADE_URL_FALLBACK = 'https://app-v3.teambuildr.com/dashboard';

// Mirrors the shape the backend's buildQuotaMessaging returns. Used only if
// the server response omits the `messaging` object entirely — the server is
// the source of truth, this just guarantees the UI always has something to
// show on the blocked path.
const fallbackMessaging = (quota) => {
  if (!quota) return null;
  const remaining = quota.allowedRemaining;
  const word = (n) => (n === 1 ? 'import' : 'imports');
  if (quota.isTrial) {
    if (remaining > 0) {
      return {
        headline: `You have ${remaining} ${word(remaining)} left on your trial.`,
        body: 'Reach out to your CSM to get more imports while on your trial.',
        cta: null,
        upgradeUrl: null,
      };
    }
    return {
      headline: 'You\'ve used all your allowed imports while on trial.',
      body: 'Reach out to your CSM to get more imports while on your trial.',
      cta: 'Or if you\'re ready to upgrade, head to your dashboard.',
      upgradeUrl: UPGRADE_URL_FALLBACK,
    };
  }
  if (quota.planCapMonthly === 0) {
    return {
      headline: 'Your plan does not include AI imports.',
      body: null,
      cta: 'Upgrade your plan to start importing programs.',
      upgradeUrl: UPGRADE_URL_FALLBACK,
    };
  }
  if (remaining > 0) {
    return {
      headline: `${remaining} ${word(remaining)} remaining (out of ${quota.planCapMonthly}).`,
      body: null,
      cta: 'Upgrade your plan for more. Questions? Reach out to your CSM.',
      upgradeUrl: UPGRADE_URL_FALLBACK,
    };
  }
  return {
    headline: `You've used all your allowed imports (${quota.planCapMonthly} on the ${quota.tier} plan).`,
    body: null,
    cta: 'Upgrade your plan for more imports. Questions? Reach out to your CSM.',
    upgradeUrl: UPGRADE_URL_FALLBACK,
  };
};

// Display title for a draft row. The uploaded filename is the headline; the
// detected program name + relative time make up the two meta lines. When the
// backend doesn't surface a filename we promote the program name to the
// headline and drop the (now duplicate) program meta line.
const draftDisplay = (item) => {
  const fileName = item.fileName || null;
  const programName = item.programName || item.originalProgramName || null;
  const title = fileName || programName || 'Untitled import';
  return {
    title,
    programName: fileName ? programName : null,
    age: formatRelativeTime(item.createdAt),
  };
};

// The yellow highlight (Figma 7230:10577, colors/yellow/200 #FFEAB5) covers the
// count phrase only — the number plus "import(s)", extended through "remaining"
// when the backend copy leads with it. Plan copy leads with the count
// ("2 imports remaining out of 5." → highlight "2 imports remaining", the rest
// plain); trial copy buries it mid-sentence ("You have 1 import left on your
// trial." → highlight "1 import"). So we return the text before/after the phrase
// as plain segments rather than assuming the count leads the line. No count
// match → nothing highlighted (the whole line renders plain).
const splitHighlight = (text) => {
  if (!text) return { before: '', mark: '', after: '' };
  const match = /\d+\s+imports?(?:\s+remaining)?/i.exec(text);
  if (!match) return { before: '', mark: '', after: text };
  const start = match.index;
  const end = start + match[0].length;
  return { before: text.slice(0, start), mark: match[0], after: text.slice(end) };
};

// Figma 7230:10510 links only the leading action word of the cta ("Upgrade"),
// not the whole sentence — the rest of the cta renders as plain bold text. The
// backend owns the copy (cta always opens with the action verb); we just peel
// off the first word as the link label. Returns null when there's no cta.
const splitCta = (cta) => {
  if (!cta) return null;
  const trimmed = cta.trimStart();
  const spaceIdx = trimmed.indexOf(' ');
  if (spaceIdx === -1) return { link: trimmed, rest: '' };
  return { link: trimmed.slice(0, spaceIdx), rest: trimmed.slice(spaceIdx) };
};

// Coach-facing names for the providers (the backend keys are anthropic/openai;
// coaches know them as Claude / ChatGPT). Returns the warning sentence with
// correct is/are agreement, or '' when nothing is down.
const describeProvidersDown = (health) => {
  if (!health || health.available !== false) return '';
  const down = [];
  if (health.anthropic?.status === 'down') down.push('Claude');
  if (health.openai?.status === 'down') down.push('ChatGPT');
  if (down.length === 0) return 'Our AI service is temporarily unavailable.';
  const subject = down.length === 2 ? `${down[0]} and ${down[1]} are` : `${down[0]} is`;
  return `AI imports are paused — ${subject} currently unavailable. Please try again in a few minutes.`;
};

const StepUploader = () => {
  const dispatch = useDispatch();
  const file = useSelector((state) => state.aiProgramImport.file);
  const isDiscovering = useSelector((state) => state.aiProgramImport.isDiscovering);
  const discoverError = useSelector((state) => state.aiProgramImport.discoverError);
  const quota = useSelector((state) => state.aiProgramImport.quota);
  const quotaError = useSelector((state) => state.aiProgramImport.quotaError);
  const pendingImports = useSelector(
    (state) => state.aiProgramImport.pendingImports || [],
  );
  const isLoadingHistoryDetail = useSelector(
    (state) => state.aiProgramImport.isLoadingHistoryDetail,
  );
  const isAbandoning = useSelector(
    (state) => state.aiProgramImport.isAbandoning,
  );
  const aiHealth = useSelector((state) => state.aiProgramImport.aiHealth);
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [resumingId, setResumingId] = useState(null);
  // Confirm-delete modal target. Holds the full pending item so we can
  // render its name inside the dialog body. null = closed.
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);
  const fileInputRef = useRef(null);
  // Synchronous re-entrancy guard for the Import button. isDiscovering (redux)
  // doesn't flip true until discoverPrograms dispatches AFTER the provider
  // health-check await resolves, so a fast double-click would otherwise fire
  // two discoverPrograms (double upload + sessionId race). The ref blocks the
  // second click immediately; isStartingImport drives the visual disabled/
  // shimmer state during the await so the button reads as busy too.
  const submitInFlightRef = useRef(false);
  const [isStartingImport, setIsStartingImport] = useState(false);

  // Resume click → hydrate Step 4 directly. loadImportForReview also
  // dispatches RESET_IMPORT internally to wipe stale wizard state, so this
  // works even mid-flight in a fresh upload. Pass the list-row name as a
  // fallback so the Review step's program-name field auto-fills even when
  // the detail endpoint omits it.
  const handleResume = useCallback(async (item) => {
    if (resumingId || isLoadingHistoryDetail) return;
    setResumingId(item.id);
    const fallbackName = item.programName || item.originalProgramName || null;
    await dispatch(loadImportForReview(item.id, fallbackName));
    setResumingId(null);
  }, [dispatch, resumingId, isLoadingHistoryDetail]);

  const handleAskDelete = useCallback((item) => {
    setPendingDeleteItem(item);
  }, []);

  const handleCancelDelete = useCallback(() => {
    if (isAbandoning) return;
    setPendingDeleteItem(null);
  }, [isAbandoning]);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDeleteItem) return;
    const result = await dispatch(abandonImport(pendingDeleteItem.id));
    if (result?.success) {
      setPendingDeleteItem(null);
    }
  }, [dispatch, pendingDeleteItem]);

  const handleFile = useCallback((incomingFile) => {
    if (!incomingFile) return;
    // Accept if EITHER the MIME type or the extension matches — the same OR
    // check the server runs. Trusting MIME alone would reject valid CSVs,
    // whose reported type varies wildly by OS/browser.
    const typeOk = incomingFile.type
      && ACCEPTED_TYPES.includes(incomingFile.type.toLowerCase());
    const extOk = ACCEPTED_EXTENSIONS.some(
      (ext) => incomingFile.name.toLowerCase().endsWith(ext),
    );
    if (!typeOk && !extOk) {
      setFileError('Please upload a spreadsheet file (.xlsx, .xls, .xlsm, or .csv).');
      return;
    }
    setFileError(null);
    dispatch(setFile(incomingFile));
  }, [dispatch]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback(() => setIsDragging(false), []);
  const handleInputChange = useCallback((e) => handleFile(e.target.files[0]), [handleFile]);
  const handleClick = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.click();
  }, []);

  // The Import button stays visually enabled (no grayed-out disabled state).
  // Pressing it without a file surfaces an inline error instead of being
  // silently dead.
  const handleNext = useCallback(async () => {
    if (isDiscovering || submitInFlightRef.current) return;
    if (!file) {
      setFileError('Please choose a spreadsheet to import first.');
      return;
    }
    if (quota && quota.allowedRemaining <= 0) return;
    setFileError(null);
    submitInFlightRef.current = true;
    setIsStartingImport(true);
    try {
      // Re-check provider liveness right before spending an import — the mount
      // check may be up to 60s stale (server cache) and a provider can drop in
      // between. Fail open: only block on an explicit available === false; a
      // failed check (success === false) falls through to discover.
      const res = await dispatch(fetchAiProviderHealth());
      if (res?.success && res.health?.available === false) return;
      dispatch(discoverPrograms(file));
    } finally {
      // On the happy path discoverPrograms has already flipped isDiscovering
      // true (synchronously, as its first dispatched action), so it takes over
      // as the guard. Release the ref/flag here either way — including the
      // down-provider short-circuit above, which returns before discover runs.
      submitInFlightRef.current = false;
      setIsStartingImport(false);
    }
  }, [file, isDiscovering, dispatch, quota]);

  // Quota hasn't resolved yet — no data and no error. This is true from the
  // first render (before accountCode resolves and the fetch even fires) right
  // through the in-flight request, so the skeleton shows immediately on refresh
  // and we never flash the hardcoded footer/notice before the backend copy
  // arrives. A failed fetch (quotaError) breaks out so the page degrades to the
  // last-resort footer instead of a stuck skeleton.
  const quotaPending = !quota && !quotaError;

  // Backend owns the copy; the FE only styles it (per product decision). The
  // backend messaging splits into headline (lead phrase) + body (remainder).
  const messaging = quota?.messaging || fallbackMessaging(quota);
  const headline = messaging?.headline || '';
  const body = messaging?.body || '';

  // Presentation is FE styling driven by the remaining count (Figma 7230 state
  // set):
  //   - used up (remaining <= 0) → tinted box (headline + body lines); the
  //     Import button is removed entirely (the dropzone stays visible).
  //   - running low (1-2 left)   → inline, headline highlighted in yellow.
  //   - plenty (> 2 left)        → inline, plain (no highlight).
  const remaining = quota?.allowedRemaining;
  const hasRemaining = Number.isFinite(remaining);
  const isUsedUp = !!quota && hasRemaining && remaining <= 0;
  const isLow = !!quota && hasRemaining && remaining > 0 && remaining <= 2;
  // When highlighting, bound the yellow to the count phrase, keeping any text
  // before/after it plain (trial copy puts the count mid-sentence).
  const {
    before: highlightBefore,
    mark: highlightMark,
    after: highlightAfter,
  } = splitHighlight(headline);

  // Footer is fully backend-driven now. The messaging object carries the
  // upgrade prose (`cta` + `upgradeUrl`) and the support nudge (`contact` +
  // `contactEmail`) as separate fields; the backend nulls `cta`/`upgradeUrl`
  // for accounts with no upgrade path (Platinum, trial happy-path), so the FE
  // no longer guesses tier eligibility. `cta` links to `upgradeUrl` (billing)
  // when present, else to support. We keep a hardcoded support line only as the
  // last-resort fallback when the backend sends no footer fields at all.
  // Only the cta's leading word links (Figma 7230:10510); the remainder is
  // plain bold text rendered right after the link.
  const ctaParts = splitCta(messaging?.cta);
  const ctaNode = ctaParts ? (
    <>
      <a
        href={messaging.upgradeUrl || `mailto:${messaging.contactEmail || SUPPORT_EMAIL}`}
        {...(messaging.upgradeUrl ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {ctaParts.link}
      </a>
      {ctaParts.rest}
    </>
  ) : null;
  // The email link carries its own trailing period inside the underline to
  // match Figma 7230:10510 ("support@teambuildr.com.").
  const contactNode = messaging?.contact && messaging?.contactEmail ? (
    <>
      {`${messaging.contact} `}
      <a href={`mailto:${messaging.contactEmail}`}>{`${messaging.contactEmail}.`}</a>
    </>
  ) : null;

  // Provider liveness — warn + block the import when the backend reports a
  // provider down. Resume/delete on existing drafts stay enabled (they don't
  // call the AI). null/unknown health is treated as available (fail open).
  const providersDown = aiHealth?.available === false;
  const healthWarning = describeProvidersDown(aiHealth);

  // Most-recent-first so the freshest draft (the one the coach most likely
  // wants to resume) leads the list — matches the Figma order.
  const sortedPending = [...pendingImports].sort((a, b) => {
    const at = new Date(a.createdAt || 0).getTime();
    const bt = new Date(b.createdAt || 0).getTime();
    return bt - at;
  });

  const hasDrafts = sortedPending.length > 0;

  return (
    <StepStack>
      <Card>
        <TitleBlock>
          <Title>Import a spreadsheet</Title>
          <Subtitle>Upload your spreadsheet and our AI will locate your program.</Subtitle>
        </TitleBlock>

        {quotaPending && <QuotaSkeleton />}

        {providersDown && healthWarning && (
          <HealthWarning role='alert'>{healthWarning}</HealthWarning>
        )}

        <DropZoneWrapper>
          <DropZone
            isDragging={isDragging}
            hasFile={!!file}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleClick}
          >
            <IconWrap hasFile={!!file}>
              <IcomoonReact
                iconSet={iconSet}
                size={24}
                icon={file ? 'checkmark' : 'cloud-upload'}
                color={file ? '#0dcc8a' : '#757575'}
              />
            </IconWrap>
            <DropZoneText>
              {file
                ? 'File selected — click to change'
                : 'Drop your spreadsheet here, or click to browse'}
            </DropZoneText>
            {!file && <DropZoneSubText>.xlsx, .xls, .xlsm, and .csv supported</DropZoneSubText>}
            {file && <FileName>{file.name}</FileName>}
            <HiddenInput
              ref={fileInputRef}
              type='file'
              accept='.xlsx,.xls,.xlsm,.csv'
              onChange={handleInputChange}
            />
          </DropZone>
        </DropZoneWrapper>

        {fileError && <ErrorText>{fileError}</ErrorText>}
        {discoverError && <ErrorText>{discoverError}</ErrorText>}

        {/* Import button drops out once imports are used up, and is disabled
            (not removed) while a provider is down so the warning above explains
            why it can't be used. */}
        {!isUsedUp && (
          <ScanButton
            disabled={isDiscovering || isStartingImport || providersDown}
            isLoading={isDiscovering || isStartingImport}
            onClick={handleNext}
          >
            {isDiscovering || isStartingImport ? 'Importing…' : 'Import'}
          </ScanButton>
        )}

        {/* Notice — backend copy, FE styling. Box when used up; inline
            (highlighted when low, plain otherwise) when imports remain. */}
        {isUsedUp && (headline || body) && (
          <Notice>
            {headline && <NoticeText>{headline}</NoticeText>}
            {body && <NoticeText>{body}</NoticeText>}
          </Notice>
        )}
        {!isUsedUp && (headline || body) && (
          <NoticeInline>
            {isLow && highlightMark ? (
              <>
                {highlightBefore}
                <NoticeMark>{highlightMark}</NoticeMark>
                {highlightAfter}
              </>
            ) : headline}
            {body && ` ${body}`}
          </NoticeInline>
        )}

        {/* Footer — backend-driven: the upgrade cta (linked to upgradeUrl) and
            the support nudge (contact + contactEmail), each rendered only when
            the backend sends it. Last-resort support line if it sends neither. */}
        <FooterRow>
          {ctaNode}
          {ctaNode && contactNode ? ' ' : null}
          {contactNode}
          {!ctaNode && !contactNode && !quotaPending && (
            <>
              {'Need more? Write us at '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{`${SUPPORT_EMAIL}.`}</a>
            </>
          )}
        </FooterRow>
      </Card>

      {hasDrafts && (
        <DraftsCard>
          <DraftsTitle as='h2'>Past imports waiting for review:</DraftsTitle>
          <DraftsList>
            {sortedPending.map((item) => {
              const { title, programName, age } = draftDisplay(item);
              const isThisResuming = resumingId === item.id;
              return (
                <DraftRow key={item.id}>
                  <DraftLeft>
                    <DraftText>
                      <DraftName
                        data-tooltip-id={DRAFT_NAME_TOOLTIP_ID}
                        data-tooltip-content={title}
                      >
                        {title}
                      </DraftName>
                      {programName && <DraftMeta title={programName}>{programName}</DraftMeta>}
                      {age && <DraftMeta>{age}</DraftMeta>}
                    </DraftText>
                    <DraftTrashButton
                      type='button'
                      aria-label={`Delete ${title}`}
                      onClick={() => handleAskDelete(item)}
                      disabled={resumingId !== null || isAbandoning}
                    >
                      <svg width='14' height='14' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
                        <path
                          d='M2 4 H14 M5.33 4 V2.67 a1 1 0 0 1 1-1 h3.33 a1 1 0 0 1 1 1 V4 M3.33 4 L4 13.33 a1 1 0 0 0 1 1 h6 a1 1 0 0 0 1-1 L12.67 4 M6.67 7.33 V12 M9.33 7.33 V12'
                          stroke='currentColor'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </DraftTrashButton>
                  </DraftLeft>
                  <DraftResumeButton
                    type='button'
                    onClick={() => handleResume(item)}
                    disabled={resumingId !== null}
                  >
                    {isThisResuming ? 'Resuming…' : 'Resume'}
                    {!isThisResuming && (
                      <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
                        <path
                          d='M6 4 L10 8 L6 12'
                          stroke='currentColor'
                          strokeWidth='1.67'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </DraftResumeButton>
                </DraftRow>
              );
            })}
          </DraftsList>
          <ReactTooltip
            id={DRAFT_NAME_TOOLTIP_ID}
            place='top'
            delayShow={120}
            style={{
              backgroundColor: 'rgba(68, 68, 68, 1)',
              color: '#FFFFFF',
              fontFamily: 'Nunito Sans',
              fontSize: '13px',
              fontWeight: 600,
              lineHeight: 1.4,
              padding: '6px 10px',
              borderRadius: '6px',
              maxWidth: '320px',
              wordBreak: 'break-word',
              zIndex: 1000,
            }}
          />
        </DraftsCard>
      )}

      {pendingDeleteItem && createPortal(
        <ConfirmOverlay
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCancelDelete();
          }}
        >
          <ConfirmCard onClick={(e) => e.stopPropagation()}>
            <ConfirmHeader>
              <ConfirmTitleRow>
                <ConfirmTitle as='h2'>
                  {`Delete “${draftDisplay(pendingDeleteItem).title}” import?`}
                </ConfirmTitle>
                <ConfirmClose
                  type='button'
                  aria-label='Close'
                  onClick={handleCancelDelete}
                  disabled={isAbandoning}
                >
                  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
                    <path
                      d='M4 4 L12 12 M12 4 L4 12'
                      stroke='currentColor'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                    />
                  </svg>
                </ConfirmClose>
              </ConfirmTitleRow>
              <ConfirmBody>
                Are you sure you want to delete this import? This action can not be undone.
              </ConfirmBody>
            </ConfirmHeader>
            <ConfirmCTA>
              <ConfirmDelete
                type='button'
                onClick={handleConfirmDelete}
                disabled={isAbandoning}
              >
                {isAbandoning ? 'Deleting…' : 'Delete'}
              </ConfirmDelete>
              <ConfirmCancel
                type='button'
                onClick={handleCancelDelete}
                disabled={isAbandoning}
              >
                Cancel
              </ConfirmCancel>
            </ConfirmCTA>
          </ConfirmCard>
        </ConfirmOverlay>,
        document.body,
      )}
    </StepStack>
  );
};

export default StepUploader;
